/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

export const getTextWidth = (text, font, isCharacter) => {
	if (!isCharacter) text += ' ';
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	context.font = font;
	const metrics = context.measureText(text);

	return metrics.width;
};

export const getTextInfo = (node, value, numLines) => {
	const words = value
		.replace(/\s+/g, ' ')
		.trim()
		.split(' ');
	const lengths = [];
	const maxPossibleLength = Math.floor(numLines) * window.innerWidth;
	const cssData = window.getComputedStyle(node, null);
	const fontData = {
		fontSize: cssData.getPropertyValue('font-size'),
		fontFamily: cssData.getPropertyValue('font-family'),
		fontWeight: cssData.getPropertyValue('font-weight'),
	};
	let cumulativeLength = 0;
	for (let i = 0; i < words.length; i++) {
		// We want to avoid getting the width of excessively many words.
		if (cumulativeLength < maxPossibleLength) {
			const width = getTextWidth(
				words[i],
				`${fontData.fontWeight} ${fontData.fontSize} ${fontData.fontFamily}`
			);
			lengths.push({
				word: words[i],
				length: width,
			});
			cumulativeLength += length;
		} else {
			break;
		}
	}
	const styles = {
		lineHeight: cssData.getPropertyValue('line-height'),
		fontInfo: `${fontData.fontWeight} ${fontData.fontSize} ${fontData.fontFamily}`,
	};
	const info = {
		lengths,
		styles,
	};

	return info;
};

export const updateTextInfo = (node, textInfo, value, numLines) => {
	const word = getTextInfo(node, value, numLines);

	textInfo.wordLengths = word.lengths;
	textInfo.ellipsisLength = getTextWidth('...', word.styles.fontInfo, true);
	textInfo.style = word.styles;

	return textInfo;
};

export const populateCharacters = (width, word, textInfo) => {
	let currentWidth = 0;
	let text = ' ';
	let i = 0;
	while (i < word.length) {
		// Can make this faster by storing character-width associations somewhere (only 26 letters in the alphabet and 10 digits), but too lazy...
		const letterWidth = getTextWidth(word.charAt(i), textInfo.style.fontInfo, true);
		if (currentWidth + letterWidth < width) {
			text += word.charAt(i);
			currentWidth += letterWidth;
			i++;
		} else {
			break;
		}
	}

	return text;
};

export const wordCanFit = (
	i,
	numWords,
	currentWidth,
	wordWidth,
	isLast,
	ellipsisLength,
	lineWidth
) =>
	i < numWords &&
	(currentWidth + wordWidth <= lineWidth - (!isLast || i === numWords - 1 ? 0 : ellipsisLength) ||
		(!currentWidth && wordWidth > lineWidth));

export const populateLine = (lineWidth, textInfo, isLast) => {
	const { wordLengths, ellipsisLength } = textInfo;
	let text = '';
	let currentWidth = 0;
	let i = 0;
	const singleWordOverflow = wordLengths[i] ? wordLengths[i].length > lineWidth : false;

	if (!singleWordOverflow) {
		while (
			i < wordLengths.length &&
			(currentWidth + wordLengths[i].length <
				lineWidth - (!isLast || i === wordLengths.length - 1 ? 0 : ellipsisLength) ||
				(!currentWidth && wordLengths[i].length > lineWidth))
		) {
			text = `${text} ${wordLengths[i].word}`;
			currentWidth += wordLengths[i].length;
			i++;
		}
	}

	if ((i < wordLengths.length && isLast) || singleWordOverflow) {
		text += populateCharacters(
			lineWidth - ellipsisLength - currentWidth,
			wordLengths[i].word,
			textInfo
		);
		if (!isLast && singleWordOverflow) i++;
	}

	const info = {
		text,
		index: i,
		singleWordOverflow,
	};

	return info;
};

export const removeFirstNElements = (obj, n) => {
	for (let i = 0; i < n; i++) {
		obj.wordLengths.shift();
	}

	return obj;
};

export const populateText = (lineWidth, numLines, textInfo) => {
	const text = [];
	for (let i = 0; i < numLines; i++) {
		const info = populateLine(lineWidth, textInfo, i === numLines - 1);
		text.push(i === numLines - 1 ? info.text.trim() : `${info.text.trim()} `);
		if (info.singleWordOverflow && i !== numLines - 1) {
			text[i] = `${text[i].trim()}...`;
		}
		textInfo = removeFirstNElements(textInfo, info.index);
	}
	const isfinishedDisplaying = textInfo.wordLengths.length !== 0;
	if (isfinishedDisplaying) {
		text[numLines - 1] = `${text[numLines - 1].trim()}...`;
	}
	const info = {
		text,
		disableTooltip: !isfinishedDisplaying,
	};

	return info;
};

export const hasOverflowed = (height, numLines, lineHeight) => height !== numLines * lineHeight;

export const checkHeight = (height, numLines, lineHeight) => {
	if (lineHeight) {
		const lineHeightValue = lineHeight.substr(0, lineHeight.length - 2);
		const parsedLineHeight = parseInt(lineHeightValue, 10);

		return hasOverflowed(height, numLines, parsedLineHeight);
	}

	return false;
};
